import { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Printer, Mail, MessageCircle, Pencil, CreditCard, Minus, CalendarClock, PackageCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Field, EmptyState } from "@/components/common";
import { ProductPicker } from "@/components/ProductPicker";
import { PaymentDialog } from "@/components/PaymentDialog";
import { SellBlocked } from "@/components/TrialBanner";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, fmtDateTime, PAY_LABELS, waLink } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export const PRE_STATUS = {
  orcamento: ["Orçamento", "bg-slate-500/15 text-slate-500"],
  aguardando_pagamento: ["Aguardando pagamento", "bg-amber-500/15 text-amber-500"],
  producao: ["Em produção", "bg-blue-500/15 text-blue-500"],
  pronta: ["Pronta", "bg-teal-500/15 text-teal-500"],
  entregue: ["Entregue", "bg-emerald-500/15 text-emerald-500"],
  cancelada: ["Cancelada", "bg-red-500/15 text-red-500"],
};
const toLocalInput = (iso) => (iso ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "");
const Badge = ({ s }) => <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", PRE_STATUS[s][1])} data-testid="preorder-status-badge">{PRE_STATUS[s][0]}</span>;

function PreorderForm({ initial, onClose, onSaved }) {
  const [customers, setCustomers] = useState([]);
  const [f, setF] = useState(() => initial?.id
    ? { ...initial, due_at: toLocalInput(initial.due_at), items: initial.items.map((i) => ({ id: i.product_id, name: i.name, price: i.price, qty: i.qty, notes: i.notes || "" })) }
    : { customer_id: null, customer: { name: "", phone: "", email: "" }, due_at: "", delivery_type: "retirada", address: "", items: [], notes: "", discount: 0, status: "orcamento" });
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);
  const setC = (k) => (e) => setF((x) => ({ ...x, customer: { ...x.customer, [k]: e.target.value } }));
  const add = (p, qty = 1) => setF((x) => ({ ...x, items: x.items.find((i) => i.id === p.id) ? x.items.map((i) => (i.id === p.id ? { ...i, qty: i.qty + qty } : i)) : [...x.items, { id: p.id, name: p.name, price: p.price, qty, notes: "" }] }));
  const upd = (id, patch) => setF((x) => ({ ...x, items: x.items.map((i) => (i.id === id ? { ...i, ...patch } : i)).filter((i) => i.qty > 0) }));
  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setF((x) => ({ ...x, customer_id: c?.id || null, customer: c ? { name: c.name, phone: c.phone || "", email: c.email || "" } : x.customer, address: c?.address || x.address }));
  };
  const subtotal = f.items.reduce((s, i) => s + i.price * i.qty, 0);
  const save = async () => {
    const body = { customer_id: f.customer_id, customer: f.customer, due_at: new Date(f.due_at).toISOString(), delivery_type: f.delivery_type, address: f.address,
      items: f.items.map((i) => ({ product_id: i.id, qty: i.qty, notes: i.notes })), notes: f.notes, discount: Number(f.discount) || 0, status: f.status };
    try {
      const { data } = initial?.id ? await api.put(`/preorders/${initial.id}`, body) : await api.post("/preorders", body);
      toast.success("Encomenda salva");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="preorder-form-modal">
        <DialogHeader><DialogTitle>{initial?.id ? `Editar encomenda #${initial.number}` : "Nova encomenda"}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-6">
          <ProductPicker compact onPick={add} />
          <div className="space-y-3">
            <Field label="Cliente cadastrado (opcional)">
              <Select value={f.customer_id || "none"} onValueChange={(v) => pickCustomer(v === "none" ? null : v)}>
                <SelectTrigger data-testid="preorder-customer-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Novo / não cadastrado</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Nome"><Input value={f.customer.name} onChange={setC("name")} data-testid="preorder-name-input" /></Field>
              <Field label="Telefone"><Input value={f.customer.phone} onChange={setC("phone")} data-testid="preorder-phone-input" /></Field>
              <Field label="E-mail"><Input value={f.customer.email} onChange={setC("email")} data-testid="preorder-email-input" /></Field>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Data e horário"><Input type="datetime-local" value={f.due_at} onChange={(e) => setF((x) => ({ ...x, due_at: e.target.value }))} data-testid="preorder-due-input" /></Field>
              <Field label="Tipo">
                <Select value={f.delivery_type} onValueChange={(v) => setF((x) => ({ ...x, delivery_type: v }))}>
                  <SelectTrigger data-testid="preorder-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="retirada">Retirada</SelectItem><SelectItem value="entrega">Entrega</SelectItem></SelectContent>
                </Select>
              </Field>
              {!initial?.id && (
                <Field label="Status inicial">
                  <Select value={f.status} onValueChange={(v) => setF((x) => ({ ...x, status: v }))}>
                    <SelectTrigger data-testid="preorder-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["orcamento", "aguardando_pagamento", "producao"].map((k) => <SelectItem key={k} value={k}>{PRE_STATUS[k][0]}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              )}
            </div>
            {f.delivery_type === "entrega" && <Field label="Endereço de entrega"><Input value={f.address} onChange={(e) => setF((x) => ({ ...x, address: e.target.value }))} data-testid="preorder-address-input" /></Field>}
            <ul className="rounded-lg border divide-y text-sm" data-testid="preorder-items-list">
              {f.items.map((i) => (
                <li key={i.id} className="px-3 py-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <button onClick={() => upd(i.id, { qty: i.qty - 1 })} data-testid={`preorder-item-dec-${i.id}`}><Minus className="h-4 w-4" /></button>
                    <input type="number" min="1" value={i.qty} onChange={(e) => upd(i.id, { qty: Math.max(1, Number(e.target.value) || 1) })} className="w-12 bg-transparent text-center font-mono outline-none" data-testid={`preorder-item-qty-${i.id}`} />
                    <span className="flex-1">{i.name}</span><span className="font-mono">{brl(i.price * i.qty)}</span>
                  </div>
                  <input value={i.notes} onChange={(e) => upd(i.id, { notes: e.target.value })} placeholder="+ observação do item" className="w-full bg-transparent text-xs text-amber-600 dark:text-amber-400 outline-none" />
                </li>
              ))}
              {!f.items.length && <li className="px-3 py-4 text-center text-muted-foreground">Selecione os produtos encomendados.</li>}
            </ul>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Desconto (R$)"><Input type="number" step="0.01" value={f.discount} onChange={(e) => setF((x) => ({ ...x, discount: e.target.value }))} data-testid="preorder-discount-input" /></Field>
              <div className="text-right self-end"><p className="text-xs text-muted-foreground">Subtotal</p><p className="font-display text-2xl font-bold">{brl(subtotal - (Number(f.discount) || 0))}</p></div>
            </div>
            <Field label="Observações"><Textarea rows={2} value={f.notes} onChange={(e) => setF((x) => ({ ...x, notes: e.target.value }))} data-testid="preorder-notes-input" /></Field>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.items.length || !f.customer.name || !f.due_at} data-testid="save-preorder-btn">Salvar encomenda</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductionOrder({ o, tenant }) {
  return (
    <div className="hidden print:block print-area p-2 font-mono text-xs">
      <p className="text-center font-bold">{tenant.nome_fantasia}</p>
      <p className="text-center font-bold">ORDEM DE PRODUÇÃO · ENCOMENDA #{o.number}</p>
      <p>Cliente: {o.customer.name} {o.customer.phone}</p>
      <p>{o.delivery_type === "entrega" ? "Entrega" : "Retirada"}: {fmtDateTime(o.due_at)}</p>
      {o.delivery_type === "entrega" && <p>Endereço: {o.address}</p>}
      <p className="border-t border-dashed my-2" />
      {o.items.map((i) => <div key={i.id}><b>{i.qty}x {i.name}</b>{i.notes && <p>  obs: {i.notes}</p>}</div>)}
      <p className="border-t border-dashed my-2" />
      {o.notes && <p>Obs.: {o.notes}</p>}
      <p>Total {brl(o.total)} · Pago {brl(o.paid)} · Restante {brl(o.remaining)}</p>
    </div>
  );
}

function Detail({ o, setO, onEdit, reload }) {
  const { session } = useAuth();
  const [pay, setPay] = useState(null);
  const final = ["entregue", "cancelada"].includes(o.status);
  const run = async (fn, msg) => {
    try {
      const { data } = await fn();
      if (data?.id) setO(data);
      toast.success(msg || data.message);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const waText = `Olá, ${o.customer.name}! Sua encomenda #${o.number} em ${session.tenant.nome_fantasia} está: ${PRE_STATUS[o.status][0]}. ${o.delivery_type === "entrega" ? "Entrega" : "Retirada"}: ${fmtDateTime(o.due_at)}. Total ${brl(o.total)}, restante ${brl(o.remaining)}.`;
  return (
    <Dialog open onOpenChange={(v) => !v && setO(null)}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="preorder-detail-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-3">Encomenda #{o.number} <Badge s={o.status} /></DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div><p className="eyebrow">Cliente</p><p className="font-medium">{o.customer.name}</p><p className="text-muted-foreground">{o.customer.phone} {o.customer.email}</p></div>
          <div><p className="eyebrow">{o.delivery_type === "entrega" ? "Entrega" : "Retirada"}</p><p className="font-medium">{fmtDateTime(o.due_at)}</p>{o.address && <p className="text-muted-foreground">{o.address}</p>}</div>
        </div>
        <ul className="rounded-lg border divide-y text-sm">{o.items.map((i) => <li key={i.id} className="flex justify-between px-3 py-2"><span>{i.qty}x {i.name}{i.notes && <span className="block text-xs text-amber-600 dark:text-amber-400">{i.notes}</span>}</span><span className="font-mono">{brl(i.total)}</span></li>)}</ul>
        {o.notes && <p className="text-sm italic">Obs.: {o.notes}</p>}
        <div className="grid grid-cols-3 gap-2 text-center" data-testid="preorder-amounts">
          <div className="rounded-lg bg-muted/60 p-3"><p className="eyebrow">Total</p><p className="font-display text-xl font-bold">{brl(o.total)}</p></div>
          <div className="rounded-lg bg-emerald-500/10 p-3"><p className="eyebrow">Entrada / pago</p><p className="font-display text-xl font-bold text-emerald-500" data-testid="preorder-paid">{brl(o.paid)}</p></div>
          <div className="rounded-lg bg-amber-500/10 p-3"><p className="eyebrow">Restante</p><p className="font-display text-xl font-bold text-amber-500" data-testid="preorder-remaining">{brl(o.remaining)}</p></div>
        </div>
        {!final && (
          <div className="flex flex-wrap gap-2">
            <Select value={o.status} onValueChange={(v) => run(() => api.patch(`/preorders/${o.id}/status`, { status: v }), "Status atualizado")}>
              <SelectTrigger className="w-56" data-testid="preorder-change-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PRE_STATUS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" disabled={!o.remaining} onClick={() => { const v = window.prompt(`Valor do pagamento (restante ${brl(o.remaining)}):`, o.remaining.toFixed(2)); if (Number(v) > 0) setPay(Math.min(Number(v), o.remaining)); }} data-testid="preorder-pay-btn"><CreditCard className="h-4 w-4 mr-1" />Registrar pagamento</Button>
            <Button variant="outline" onClick={onEdit} data-testid="preorder-edit-btn"><Pencil className="h-4 w-4 mr-1" />Editar</Button>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="preorder-print-btn"><Printer className="h-4 w-4 mr-1" />Ordem de produção</Button>
          <Button variant="outline" disabled={!o.customer.email} onClick={() => run(() => api.post(`/preorders/${o.id}/notify`))} data-testid="preorder-notify-email-btn"><Mail className="h-4 w-4 mr-1" />Avisar por e-mail</Button>
          <Button asChild variant="outline" data-testid="preorder-notify-whatsapp-btn">
            <a href={waLink(o.customer.phone, waText)} target="_blank" rel="noreferrer" onClick={() => api.post(`/preorders/${o.id}/log-whatsapp`).then(reload)}><MessageCircle className="h-4 w-4 mr-1" />WhatsApp</a>
          </Button>
        </div>
        {o.payments.length > 0 && <ul className="text-xs text-muted-foreground">{o.payments.map((p, k) => <li key={k}>{fmtDateTime(p.at)} · {PAY_LABELS[p.method]} {brl(p.amount)} · {p.user}</li>)}</ul>}
        <div>
          <p className="eyebrow mb-2">Histórico de alterações</p>
          <ul className="space-y-2 text-sm max-h-48 overflow-y-auto" data-testid="preorder-history">{[...o.history].reverse().map((h, k) => <li key={k} className="border-l-2 border-primary/40 pl-3"><p>{h.action}</p><p className="text-xs text-muted-foreground">{fmtDateTime(h.at)} · {h.user}</p></li>)}</ul>
        </div>
        <ProductionOrder o={o} tenant={session.tenant} />
        <PaymentDialog open={pay !== null} onOpenChange={(v) => !v && setPay(null)} subtotal={pay || 0} allowDiscount={false} title={`Pagamento encomenda #${o.number}`}
          onConfirm={async ({ payments }) => {
            let change = Math.round((payments.reduce((s, p) => s + p.amount, 0) - pay) * 100) / 100;
            const adjusted = payments.map((p) => { if (change > 0 && p.method === "dinheiro") { const cut = Math.min(change, p.amount); change -= cut; return { ...p, amount: p.amount - cut }; } return p; }).filter((p) => p.amount > 0);
            const { data } = await api.post(`/preorders/${o.id}/payments`, { payments: adjusted });
            setO(data);
            reload();
            toast.success("Pagamento registrado");
          }} />
      </DialogContent>
    </Dialog>
  );
}

export default function Preorders() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("abertas");
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get("/preorders").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => list.filter((o) => (filter === "abertas" ? !["entregue", "cancelada"].includes(o.status) : o.status === filter)), [list, filter]);

  return (
    <>
      <PageHeader eyebrow="Produção" title="Encomendas" subtitle="Vendas sob encomenda com entrada, produção e entrega."
        actions={<Button onClick={() => setForm({})} data-testid="new-preorder-btn"><Plus className="h-4 w-4 mr-1" />Nova encomenda</Button>} />
      <SellBlocked />
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[["abertas", "Em aberto"], ...Object.entries(PRE_STATUS).map(([k, [l]]) => [k, l])].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`preorder-filter-${k}`}
            className={cn("shrink-0 rounded-full border px-4 py-2 text-sm font-medium touch", filter === k ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent")}>
            {l} <span className="font-mono text-xs opacity-70">{k === "abertas" ? list.filter((o) => !["entregue", "cancelada"].includes(o.status)).length : list.filter((o) => o.status === k).length}</span>
          </button>
        ))}
      </div>
      {!shown.length ? <EmptyState icon={PackageCheck} title="Nenhuma encomenda" text="Registre bolos, kits, pedidos grandes e serviços com data de retirada ou entrega." /> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="preorders-list">
          {shown.map((o) => (
            <button key={o.id} onClick={() => setDetail(o)} className="card-surface card-hover p-4 text-left space-y-2" data-testid={`preorder-card-${o.number}`}>
              <div className="flex items-center justify-between"><span className="font-mono font-bold">#{o.number}</span><Badge s={o.status} /></div>
              <p className="font-semibold">{o.customer.name}</p>
              <p className="flex items-center gap-1 text-sm text-muted-foreground"><CalendarClock className="h-4 w-4" />{o.delivery_type === "entrega" ? "Entrega" : "Retirada"} · {fmtDateTime(o.due_at)}</p>
              <p className="text-xs text-muted-foreground line-clamp-1">{o.items.map((i) => `${i.qty}x ${i.name}`).join(", ")}</p>
              <div className="flex justify-between border-t pt-2 text-sm"><span>Total <b className="font-mono">{brl(o.total)}</b></span><span className="text-amber-500">Restante <b className="font-mono">{brl(o.remaining)}</b></span></div>
            </button>
          ))}
        </div>
      )}
      {form && <PreorderForm initial={form} onClose={() => setForm(null)} onSaved={(d) => { setForm(null); setDetail(d); load(); }} />}
      {detail && !form && <Detail o={detail} setO={setDetail} reload={load} onEdit={() => setForm(detail)} />}
    </>
  );
}
